import React from "react";
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider, Calendar } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import moment from "moment";
import classNames from "classnames";
import styles from "./dashboard.module.css";
import CircleIcon from "@mui/icons-material/Circle";
import { Paper } from "@material-ui/core";

const theme = createTheme({
    palette: {
        primary: { light: "#56E4D7", main: "#56E4D7", dark: "#56E4D7" },
    },
    overrides: {
        MuiPickersCalendarHeader: {
            switchHeader: {
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
                padding: "0 8px",
            },
            transitionContainer: {
                position: "relative",
                top: "auto",
                left: "auto",
                transform: "none",
                order: -1,
                textAlign: "left",
                width: "auto",
                flex: "1",
                "& > p": {
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#444444",
                },
            },
            iconButton: {
                padding: "4px",
                order: 1,
                "& > *": {
                    color: "#444444",
                    fontSize: "22px",
                },
            },
            daysHeader: {
                display: "flex",
                justifyContent: "space-between",
                padding: "0 8px",
            },
            dayLabel: {
                width: "40px",
                textAlign: "center",
                fontFamily: "Poppins",
                fontSize: "14px",
                color: "#444444",
            },
        },
        MuiPickersCalendar: {
            week: {
                display: "flex",
                justifyContent: "space-between",
                padding: "0 8px",
            },
        },
        MuiPickersDay: {
            day: {
                width: "40px",
                height: "40px",
                margin: "0",
            },
            daySelected: {
                backgroundColor: "transparent !important",
                color: "#444444 !important",
                fontWeight: 400,
            },
            current: {
                color: "#444444",
                fontWeight: 400,
            },
        },
        MuiPickersBasePicker: {
            pickerView: {
                maxWidth: "100%",
            },
        },
        MuiTypography: {
            alignCenter: {
                textAlign: "left",
            },
        },
    },
});

export default function MainCalendar({ selectedDays, date, setDate, getJournalData }) {

    // Track the currently displayed month (may differ from selected date when navigating)
    const [displayedMonth, setDisplayedMonth] = React.useState(date || new Date());

    // Check if we're viewing the current month (to disable right arrow)
    const isCurrentMonth = React.useMemo(() => {
        const now = new Date();
        const displayed = displayedMonth || date || new Date();
        return displayed.getMonth() === now.getMonth() && displayed.getFullYear() === now.getFullYear();
    }, [displayedMonth, date]);

    const handleMonthChange = (newMonth) => {
        if (!getJournalData) {
            setDisplayedMonth(newMonth);
            return;
        }

        // Compare against the currently displayed month, not the selected date
        const currentDisplayedMonth = displayedMonth?.getMonth?.();
        const currentDisplayedYear = displayedMonth?.getFullYear?.();
        const newMonthIndex = newMonth?.getMonth?.();
        const newYear = newMonth?.getFullYear?.();

        // Update the displayed month state
        setDisplayedMonth(newMonth);

        // If month and year haven't actually changed, skip calling getJournalData
        if (currentDisplayedMonth === newMonthIndex && currentDisplayedYear === newYear) {
            return;
        }

        // Load journal data for the new month
        getJournalData(newMonth);
    };

    return (
        <div
            style={{
                backgroundColor: "#ffffff",
                padding: "5px",
                margin: 0,
                width: "100%",
                overflow: "hidden",
                boxSizing: "border-box",
                border: "1px solid #E9F1F1",
                borderRadius: "5px",
                boxShadow: "0px 2.4px 4.8px rgba(0, 0, 0, 0.06)",
            }}
        >
            <MuiThemeProvider theme={theme}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Paper style={{ 
                        overflow: "hidden", 
                        boxShadow: 'none',
                        padding: "8px 0",
                        width: "100%"
                    }}>
                        <Calendar
                            date={date}
                            disableFuture={false}
                            disablePast={false}
                            maxDate={new Date()}
                            onChange={(newDate) => setDate(newDate)}
                            onMonthChange={handleMonthChange}
                            rightArrowButtonProps={{ 
                                disabled: isCurrentMonth,
                                style: isCurrentMonth ? { opacity: 0.3, pointerEvents: 'none' } : {}
                            }}
                            leftArrowButtonProps={{ disabled: false }}
                            style={{
                                display: "flex",
                                flexDirection: "column"
                            }}
                            renderDay={(
                                day,
                                selectedDate,
                                isInCurrentMonth,
                                dayComponent
                            ) => {
                                const selectDate = moment(day).format("YYYY-MM-DD");
                                const hasEntry = selectedDays.includes(selectDate) && isInCurrentMonth;
                                const isToday = moment(day).isSame(new Date(), 'day');
                                const isActiveDate = moment(day).isSame(date, 'day');

                                return (
                                    <div
                                        className={classNames(styles.calendarDay, {
                                            [styles.calendarDayToday]: isToday && !isActiveDate,
                                            [styles.calendarDaySelected]: isActiveDate,
                                        })}
                                    >
                                        {dayComponent}
                                        {hasEntry && (
                                            <CircleIcon
                                                style={{
                                                    fontSize: "0.15rem",
                                                    color: "black",
                                                    padding: 0,
                                                    marginBottom: 1,
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            }}
                        />
                    </Paper>
                </MuiPickersUtilsProvider>
            </MuiThemeProvider>
        </div>
    );

}
